import React, { useCallback, useMemo, useState } from 'react'
import axios from 'axios'
import { format } from 'date-fns'
import { Typography } from '@mui/material'
import config from 'config'
import { useTranslation } from 'react-i18next'
import theme from 'theme'

import useSnackbar from 'components/organisms/Snackbar/useSnackbar'

import {
  ActionDatePicker,
  ActionDocumentUpload,
  ActionInput,
  ActionSwitch,
} from '../components/atoms/Actions'

/**
 *
 * @param signUrl the url required for the signupload. Eg. http://localhost:3001/inventory/sign-upload-document
 * @param file The File object received from the `input` html element
 * @returns {Promise<string>} The name of the file uploaded. Eg. 652415ba-3a73-40b4-859d-50d2a256817c.pdf
 */
const uploadFile = async (signUrl, file, t) => {
  if (!file) return

  const result = await axios.get(`${signUrl}?filename=${file.name}`)

  if (!result.data?.body?.signedUrl) {
    throw new Error(t('common.mappedAction.errors.failedSigning'))
  }

  const data = new FormData()
  data.append('img', file, file.name)

  const uploadConfig = {
    headers: { 'Content-Type': 'application/pdf' },
  }

  const res = await fetch(
    result.data.body.signedUrl,
    { body: data, method: 'PUT' },
    uploadConfig
  )

  if (res.status !== 200) {
    throw new Error(t('common.mappedAction.errors.failedUploading'))
  }

  return result.data.body.fileName
}

export const useMappedActions = (actions = {}) => {
  const { t } = useTranslation()

  const { showSnackbarMessage } = useSnackbar()

  const mappedActions = useMemo(() => {
    return actions ?? {}
  }, [actions])

  const [document1, setDocument1] = useState()
  const [document2, setDocument2] = useState()
  const [document3, setDocument3] = useState()
  const [date1, setDate1] = useState()
  const [number1, setNumber1] = useState()
  const [number2, setNumber2] = useState()
  const [string1, setString1] = useState()
  const [boolean1, setBoolean1] = useState()
  const [inLineError, setInLineError] = useState()

  const readFileAndSendData = useCallback(
    (file, setDocument) => {
      const reader = new FileReader()
      reader.onerror = () =>
        showSnackbarMessage({
          severity: 'error',
          message: t('common.error.fileUpload.failedReading'),
        })
      reader.onload = async () => {
        try {
          setDocument({ fileName: file.name, file, buffer: reader.result })
        } catch (error) {
          showSnackbarMessage({
            severity: 'error',
            message: t('common.error.fileUpload.failedReading'),
          })
          return
        }
      }
      reader.readAsArrayBuffer(file)
    },
    [showSnackbarMessage, t]
  )

  const getFormError = useCallback(
    (action, item) => {
      const document1Component = mappedActions[action]?.document1
      const document2Component = mappedActions[action]?.document2
      const document3Component = mappedActions[action]?.document3
      const date1Component = mappedActions[action]?.date1
      const number1Component = mappedActions[action]?.number1
      const number2Component = mappedActions[action]?.number2

      const validationMandatoryDocumentLabel =
        'common.mappedAction.validation.mandatory.file'

      if (number1Component && !number1) {
        return t('common.mappedAction.validation.mandatory.number', {
          attribute: number1Component.label,
        })
      }

      if (number2Component && !number2) {
        return t('common.mappedAction.validation.mandatory.number', {
          attribute: number2Component.label,
        })
      }

      if (date1Component && !date1) {
        return t('common.mappedAction.validation.mandatory.date', {
          attribute: date1Component.label,
        })
      }

      if (document1Component && !document1Component.optional && !document1) {
        return t(validationMandatoryDocumentLabel, {
          attribute: document1Component.label,
        })
      }

      if (document2Component && !document2Component.optional && !document2) {
        return t(validationMandatoryDocumentLabel, {
          attribute: document2Component.label,
        })
      }

      if (document3Component && !document3Component.optional && !document3) {
        return t(validationMandatoryDocumentLabel, {
          attribute: document3Component.label,
        })
      }

      if (mappedActions?.[action]?.validate) {
        return mappedActions[action].validate(item, {
          number1,
          number2,
          date1,
          document1,
          document2,
          document3,
          string1,
          boolean1,
        })
      }
    },
    [
      mappedActions,
      number1,
      number2,
      date1,
      document1,
      document2,
      document3,
      t,
      string1,
      boolean1,
    ]
  )

  const getActionAdditionalParameters = useCallback(
    async (action) => {
      const additionalParms = {}
      const document1Component = mappedActions[action]?.document1
      const document2Component = mappedActions[action]?.document2
      const document3Component = mappedActions[action]?.document3
      const date1Component = mappedActions[action]?.date1
      const number1Component = mappedActions[action]?.number1
      const number2Component = mappedActions[action]?.number2
      const string1Component = mappedActions[action]?.string1
      const boolean1Component = mappedActions[action]?.boolean1

      if (date1Component) {
        additionalParms.date1 = format(date1, 'yyyy-MM-dd')
      }

      if (number1Component) {
        additionalParms.number1 = number1.trim()
      }

      if (number2Component) {
        additionalParms.number2 = number2.trim()
      }

      if (string1Component) {
        additionalParms.string1 = (string1 || '').trim()
      }

      if (boolean1Component) {
        additionalParms.boolean1 = boolean1
      }

      if (document1Component && document1?.file) {
        const result = await uploadFile(
          `${config.apiBaseUrl}inventory/sign-upload-document`,
          document1.file,
          t
        )

        additionalParms.document1 = result
      }

      if (document2Component && document2?.file) {
        const result = await uploadFile(
          `${config.apiBaseUrl}inventory/sign-upload-document`,
          document2.file,
          t
        )

        additionalParms.document2 = result
      }

      if (document3Component && document3?.file) {
        const result = await uploadFile(
          `${config.apiBaseUrl}inventory/sign-upload-document`,
          document3.file,
          t
        )

        additionalParms.document3 = result
      }

      return additionalParms
    },
    [
      mappedActions,
      document1?.file,
      document2?.file,
      document3?.file,
      date1,
      number1,
      number2,
      string1,
      boolean1,
      t,
    ]
  )

  const resetForm = useCallback(() => {
    setDate1(new Date())
    setDocument1()
    setDocument2()
    setDocument3()
    setNumber1()
    setNumber2()
    setString1('')
    setInLineError()
  }, [
    setDate1,
    setDocument1,
    setDocument2,
    setDocument3,
    setNumber1,
    setNumber2,
    setString1,
    setInLineError,
  ])

  const getConfirmationFormFields = useCallback(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    (action, readOnly, item) => {
      const getHeader = mappedActions[action]?.getHeader
      const document1Component = mappedActions[action]?.document1
      const document2Component = mappedActions[action]?.document2
      const document3Component = mappedActions[action]?.document3
      const date1Component = mappedActions[action]?.date1
      const number1Component = mappedActions[action]?.number1
      const number2Component = mappedActions[action]?.number2
      const string1Component = mappedActions[action]?.string1
      const boolean1Component = mappedActions[action]?.boolean1

      const returnElement = []

      if (getHeader) {
        returnElement.push(
          <React.Fragment key="action-header">
            {getHeader(item, t)}
          </React.Fragment>
        )
      }
      returnElement.push(
        number1Component ? (
          <ActionInput
            key="action-number-1"
            readOnly={readOnly}
            resetError={setInLineError}
            type="number"
            onChange={(newValue) => setNumber1(newValue)}
            value={number1}
            label={number1Component.label}
            {...(number1Component.getProps
              ? number1Component.getProps(item)
              : {})}
          />
        ) : null
      )

      returnElement.push(
        number2Component ? (
          <ActionInput
            key="action-number-2"
            readOnly={readOnly}
            resetError={setInLineError}
            type="number"
            onChange={(newValue) => setNumber2(newValue)}
            value={number2}
            label={number2Component.label}
            {...(number2Component.getProps
              ? number2Component.getProps(item)
              : {})}
          />
        ) : null
      )

      returnElement.push(
        string1Component ? (
          <ActionInput
            key="action-string-1"
            readOnly={readOnly}
            resetError={setInLineError}
            onChange={(newValue) => setString1(newValue)}
            value={string1}
            label={string1Component.label}
            {...(string1Component.getProps
              ? string1Component.getProps(item)
              : {})}
          />
        ) : null
      )

      returnElement.push(
        boolean1Component ? (
          <ActionSwitch
            key="action-boolean-1"
            readOnly={readOnly}
            resetError={setInLineError}
            onChange={(newValue) => setBoolean1(newValue)}
            value={boolean1 ?? false}
            label={boolean1Component.label}
            labelLeft={boolean1Component.labelLeft}
            {...(boolean1Component.getProps
              ? boolean1Component.getProps(item)
              : {})}
          />
        ) : null
      )

      returnElement.push(
        date1Component ? (
          <ActionDatePicker
            key="action-date-1"
            readOnly={readOnly}
            resetError={setInLineError}
            onChange={(newDate) => setDate1(newDate)}
            value={date1}
            label={date1Component.label}
            {...(date1Component.getProps ? date1Component.getProps(item) : {})}
          />
        ) : null
      )
      returnElement.push(
        document1Component ? (
          <ActionDocumentUpload
            key="action-document-1"
            readOnly={readOnly}
            resetError={setInLineError}
            readFileAndSendData={(file) =>
              readFileAndSendData(file, setDocument1)
            }
            value={document1}
            label={document1Component.label}
            loadLabel={document1Component.loadLabel}
            onReset={() => setDocument1()}
            {...(document1Component.getProps
              ? document1Component.getProps(item)
              : {})}
          />
        ) : null
      )
      returnElement.push(
        document2Component ? (
          <ActionDocumentUpload
            key="action-document-2"
            readOnly={readOnly}
            resetError={setInLineError}
            readFileAndSendData={(file) =>
              readFileAndSendData(file, setDocument2)
            }
            value={document2}
            onReset={() => setDocument2()}
            label={document2Component.label}
            loadLabel={document2Component.loadLabel}
            {...(document2Component.getProps
              ? document2Component.getProps(item)
              : {})}
          />
        ) : null
      )
      returnElement.push(
        document3Component ? (
          <ActionDocumentUpload
            key="action-document-3"
            readOnly={readOnly}
            resetError={setInLineError}
            readFileAndSendData={(file) =>
              readFileAndSendData(file, setDocument3)
            }
            value={document3}
            onReset={() => setDocument3()}
            label={document3Component.label}
            loadLabel={document3Component.loadLabel}
            {...(document3Component.getProps
              ? document3Component.getProps(item)
              : {})}
          />
        ) : null
      )

      if (inLineError)
        returnElement.push(
          <Typography variant="body2" color={theme.palette.error.main}>
            {inLineError}
          </Typography>
        )

      return <>{returnElement}</>
    },
    [
      readFileAndSendData,
      document1,
      setDocument1,
      document2,
      setDocument2,
      document3,
      setDocument3,
      date1,
      setDate1,
      number1,
      setNumber1,
      number2,
      setNumber2,
      string1,
      setString1,
      boolean1,
      setBoolean1,
      inLineError,
      setInLineError,
      t,
      mappedActions,
    ]
  )

  return {
    mappedActions,
    setInLineError,
    getFormError,
    getActionAdditionalParameters,
    resetForm,
    getConfirmationFormFields,
  }
}
